import { ReactNode, useEffect, useMemo } from "react";
import classNames from "classnames";
import { ChartOfAccountsTemplateStartingPoint as StartingPoint } from "@common/constants/domains/accounting/chartOfAccountsTemplateStartingPoint";
import { isEntityAllowedAsCopySource } from "@common/helpers/chartOfAccountsTemplateApplicability";
import { ChartOfAccountsTemplate } from "@common/types/chartOfAccountsTemplate";
import {
    BusinessIcon,
    ChartOfAccountsIcon,
    DocumentIcon,
    UploadIcon,
} from "@icons";
import { useScope } from "@state/scopeContext.lib";
import { useWorkspacesList } from "@api/workspace.api";
import { useChartOfAccountsTemplates } from "@api/coaTemplate.api";
import { Picker } from "@design-system/Picker/Picker.lib";
import {
    ChartOfAccountsTemplateStartingPointSelection,
    defaultStartingPointSelection,
} from "./chartOfAccountsTemplateStartingPoint.utils";
import { findEntityInWorkspaces } from "./findEntityInWorkspaces";
import styles from "./ChartOfAccountsTemplateModal.module.scss";

interface TileConfig {
    id: StartingPoint;
    icon: ReactNode;
    title: string;
    description: string;
}

const tiles: TileConfig[] = [
    {
        id: StartingPoint.default,
        icon: <DocumentIcon />,
        title: "Default Kick template",
        description: "Start with Kick's default accounts.",
    },
    {
        id: StartingPoint.copyTemplate,
        icon: <ChartOfAccountsIcon />,
        title: "Copy from template",
        description:
            "Duplicate an existing template and customize. Fastest for new verticals.",
    },
    {
        id: StartingPoint.copyEntity,
        icon: <BusinessIcon />,
        title: "Copy from entity",
        description:
            "Mirror an entity's current chart of accounts. Use when a client's chart is the gold standard.",
    },
    {
        id: StartingPoint.importCoa,
        icon: <UploadIcon />,
        title: "Import your own",
        description: "Import your firm's standard accounts from a spreadsheet.",
    },
];

const TEMPLATE_PLACEHOLDER = "Select template";
const ENTITY_PLACEHOLDER = "Select entity";

const selectControlTrigger = (
    <Picker.Button variant="primary" size="lg" block />
);

interface Props {
    value: ChartOfAccountsTemplateStartingPointSelection;
    onChange: (value: ChartOfAccountsTemplateStartingPointSelection) => void;
    duplicateSource?: ChartOfAccountsTemplate;
}

export const ChartOfAccountsTemplateStartingPoint = ({
    value,
    onChange,
    duplicateSource,
}: Props) => {
    const { organizationId } = useScope();
    const { data: activeTemplates, isFetched: areTemplatesFetched } =
        useChartOfAccountsTemplates();
    const { data: workspaces, isFetched: areWorkspacesFetched } =
        useWorkspacesList({
            organizationId: organizationId ?? undefined,
            includeEntities: true,
        });

    const entityGroups = useMemo(
        () =>
            (workspaces ?? [])
                .map((workspace) => ({
                    id: workspace.id,
                    name: workspace.name,
                    entities: (workspace.entities ?? []).filter((entity) =>
                        isEntityAllowedAsCopySource({
                            sourceEntity: entity,
                        }),
                    ),
                }))
                .filter((group) => group.entities.length > 0),
        [workspaces],
    );

    const entitySections = useMemo(
        () =>
            entityGroups.map((group) => ({
                key: group.id,
                title: group.name,
                items: group.entities.map((entity) => ({
                    value: entity.id,
                    label: entity.name ?? "Untitled entity",
                })),
            })),
        [entityGroups],
    );
    const templates = useMemo(() => {
        const listedTemplates = activeTemplates ?? [];
        return duplicateSource &&
            !listedTemplates.some(({ id }) => id === duplicateSource.id)
            ? [...listedTemplates, duplicateSource]
            : listedTemplates;
    }, [activeTemplates, duplicateSource]);
    const templateItems = useMemo(
        () =>
            templates.map((template) => ({
                value: template.id,
                label: template.name,
            })),
        [templates],
    );

    const hasTemplates = templates.length > 0;
    const hasEntities = entityGroups.length > 0;

    const isDisabledById: Record<StartingPoint, boolean> = {
        [StartingPoint.default]: false,
        [StartingPoint.copyTemplate]: areTemplatesFetched && !hasTemplates,
        [StartingPoint.copyEntity]: areWorkspacesFetched && !hasEntities,
        [StartingPoint.importCoa]: false,
    };

    const isSelectedDisabled = isDisabledById[value.startingPoint];

    useEffect(() => {
        if (isSelectedDisabled) {
            onChange(defaultStartingPointSelection);
        }
    }, [isSelectedDisabled, onChange]);

    const selectedTemplateName = templates.find(
        (template) => template.id === value.sourceTemplateId,
    )?.name;

    const selectedEntityName = findEntityInWorkspaces(
        workspaces,
        value.sourceEntityId,
    )?.name;

    return (
        <section className={styles.startingPoint}>
            <span className={styles.startingPointLabel}>Starting point</span>
            <div className={styles.tiles}>
                {tiles.map(({ id, icon, title, description }) => (
                    <button
                        key={id}
                        type="button"
                        aria-pressed={value.startingPoint === id}
                        disabled={isDisabledById[id]}
                        onClick={() =>
                            onChange({
                                startingPoint: id,
                                sourceTemplateId: null,
                                sourceEntityId: null,
                            })
                        }
                        className={classNames(styles.tile, {
                            [styles.tileSelected]:
                                value.startingPoint === id &&
                                !isDisabledById[id],
                        })}
                    >
                        <span className={styles.tileIcon}>{icon}</span>
                        <span className={styles.tileTitle}>{title}</span>
                        <span className={styles.tileDescription}>
                            {description}
                        </span>
                    </button>
                ))}
            </div>

            {value.startingPoint === StartingPoint.copyTemplate && (
                <div className={styles.sourceField}>
                    <span className={styles.startingPointLabel}>
                        Source template
                    </span>
                    <Picker.Select
                        items={templateItems}
                        value={value.sourceTemplateId}
                        onValueChange={(sourceTemplateId) =>
                            onChange({ ...value, sourceTemplateId })
                        }
                        renderValue={() =>
                            selectedTemplateName ?? TEMPLATE_PLACEHOLDER
                        }
                        placeholder={TEMPLATE_PLACEHOLDER}
                        trigger={selectControlTrigger}
                    />
                </div>
            )}

            {value.startingPoint === StartingPoint.copyEntity && (
                <div className={styles.sourceField}>
                    <span className={styles.startingPointLabel}>
                        Source entity
                    </span>
                    <Picker.Select
                        sections={entitySections}
                        value={value.sourceEntityId}
                        onValueChange={(sourceEntityId) =>
                            onChange({ ...value, sourceEntityId })
                        }
                        renderValue={() =>
                            selectedEntityName ?? ENTITY_PLACEHOLDER
                        }
                        placeholder={ENTITY_PLACEHOLDER}
                        trigger={selectControlTrigger}
                    />
                </div>
            )}
        </section>
    );
};
